import { createContext } from 'react';
import type { DirectLineJSBotConnection, WebChatActivity } from 'botframework-webchat-core';

import { AttachmentForScreenReaderComponentFactory } from './druid/./druid/types/AttachmentForScreenReaderMiddleware';
import { AvatarComponentFactory } from './druid/./druid/types/AvatarMiddleware';
import { GroupActivities } from './druid/./druid/types/GroupActivitiesMiddleware';
import { LegacyActivityRenderer } from './druid/./druid/types/ActivityMiddleware';
import { Observable } from 'redux';
import { PerformCardAction } from './druid/./druid/types/CardActionMiddleware';
import { RenderActivityStatus } from './druid/./druid/types/ActivityStatusMiddleware';
import { RenderAttachment } from './druid/./druid/types/AttachmentMiddleware';
import { RenderToast } from './druid/./druid/types/ToastMiddleware';
import { ScrollToEndButtonComponentFactory } from './druid/./druid/types/ScrollToEndButtonMiddleware';
import { StrictStyleOptions } from './druid/./druid/StyleOptions';
import LocalizedStrings from './druid/./druid/types/LocalizedStrings';
import PrecompiledGlobalize from './druid/./druid/types/PrecompiledGlobalize';
import TelemetryMeasurementEvent from './druid/./druid/types/TelemetryMeasurementEvent';

type WebChatAPIContext = {
  activityRenderer?: LegacyActivityRenderer;
  activityStatusRenderer: RenderActivityStatus;
  attachmentForScreenReaderRenderer?: AttachmentForScreenReaderComponentFactory;
  attachmentRenderer?: RenderAttachment;
  avatarRenderer?: AvatarComponentFactory;
  clearSuggestedActions?: () => void;
  dir?: string;
  directLine?: DirectLineJSBotConnection;
  disabled?: boolean;
  dismissNotification?: (id: string) => void;
  downscaleImageToDataURL?: (blob: Blob, maxWidth: number, maxHeight: number, type: string, quality: number) => string;
  emitTypingIndicator?: () => void;
  grammars?: any;
  groupActivities?: GroupActivities;
  internalErrorBoxClass?: React.Component | Function;
  language?: string;
  localizedGlobalizeState?: PrecompiledGlobalize[];
  localizedStrings?: { [language: string]: LocalizedStrings };
  markActivity?: (activity: { id: string }, name: string, value?: any) => void;
  onCardAction?: PerformCardAction;
  onTelemetry?: (event: TelemetryMeasurementEvent) => void;
  postActivity?: (activity: WebChatActivity) => Observable<string>;
  renderMarkdown?: (
    markdown: string,
    newLineOptions: { markdownRespectCRLF: boolean },
    linkOptions: { externalLinkAlt: string }
  ) => string;
  scrollToEndButtonRenderer?: ScrollToEndButtonComponentFactory;
  selectVoice?: (voices: (typeof window.SpeechSynthesisVoice)[], activity: WebChatActivity) => void;
  sendEvent?: (name: string, value: any) => void;
  sendFiles?: (files: File[]) => void;
  sendMessage?: (text: string, method?: string, { channelData }?: { channelData?: any }) => void;
  sendMessageBack?: (value: any, text?: string, displayText?: string) => void;
  sendPostBack?: (value?: any) => void;
  sendTypingIndicator?: boolean;
  setDictateInterims?: (interims: string[]) => void;
  setDictateState?: (dictateState: number) => void;
  setNotification?: (notification: Notification) => void;
  setSendBox?: (value: string) => void;
  setSendTimeout?: (timeout: number) => void;
  startDictate?: () => void;
  startSpeakingActivity?: () => void;
  stopDictate?: () => void;
  stopSpeakingActivity?: () => void;
  styleOptions?: StrictStyleOptions;
  submitSendBox?: (method?: string, { channelData }?: { channelData: any }) => void;
  telemetryDimensionsRef?: React.Ref<any>;
  toastRenderer?: RenderToast;
  trackDimension?: (name: string, data: any) => void;
  typingIndicatorRenderer?: any; // TODO
  userID?: string;
  username?: string;
};

const context = createContext<WebChatAPIContext>(undefined);

context.displayName = 'WebChatAPIContext';

export default context;
